import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  getCausadores,
  getCausasPrimarias,
  getCausasSecundarias,
  salvarExecucao,
} from "../api";
import "./ReprogramarModal.css";

/* Utils */
const uniqSorted = (arr) =>
  Array.from(new Set((arr || []).filter(Boolean))).sort((a, b) =>
    String(a).localeCompare(String(b), "pt-BR")
  );

const CORRECOES = [
  [/\brpévia\b/gi, "prévia"],
  [/\bprevia\b/gi, "prévia"],
  [/\bprim[ée]ria\b/gi, "primeira"],
  [/\bsegubda\b/gi, "segunda"],
  [/\binicio\b/gi, "início"],
  [/\btermino\b/gi, "término"],
  [/\bobs\b/gi, "observação"],
];
const corrigePT = (txt = "") => {
  let out = String(txt || "");
  for (const [re, rep] of CORRECOES) out = out.replace(re, rep);
  return out.replace(/\s{2,}/g, " ").trim();
};

/* Apenas os campos aceitos pelo backend */
const sanitizePayload = (raw) => {
  const allowed = new Set([
    "codigo_unico",
    "etapa_nova",
    "status_programacaod1",
    "motivo_alteracao",

    // campos de fechamento / desvios
    "responsavel",
    "impacto_fechamento",
    "causa_fechamento",
    "justificativa_fechamento",

    // limpeza da execução
    "status_finalizacao",
    "primeira_previa",
    "segunda_previa",
    "primeiro_inicio",
    "primeiro_fim",
    "segundo_inicio",
    "segundo_fim",
    "terceiro_inicio",
    "terceiro_fim",
    "quantidade_real",
    "tempo_total",
  ]);
  const clean = {};
  for (const [k, v] of Object.entries(raw || {})) {
    if (!allowed.has(k)) continue;
    if (v === undefined) continue;
    clean[k] = v;
  }
  return clean;
};

export default function ReprogramarModal({
  codigo_unico,           // OBRIGATÓRIO
  onClose = () => {},
  onSaved = () => {},     // chamado após sucesso
  onConfirm,              // compat: se vier, também é chamado no sucesso
}) {
  const [form, setForm] = useState({
    responsabilidade: "",
    impacto: "",
    causa: "",
    justificativa: "",
  });
  const [causadores, setCausadores] = useState([]);
  const [primarias, setPrimarias] = useState([]);
  const [secundarias, setSecundarias] = useState([]);
  const [loading, setLoading] = useState(false);

  /* Fechar pelo ESC (não fecha clicando no backdrop) */
  const onKeyDown = useCallback(
    (e) => { if (e.key === "Escape") onClose(); },
    [onClose]
  );
  useEffect(() => {
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onKeyDown]);

  /* Carrega causadores (responsabilidades) */
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const list = await getCausadores();
        if (alive) setCausadores(uniqSorted(list));
      } catch {
        if (alive) setCausadores([]);
      }
    })();
    return () => { alive = false; };
  }, []);

  /* Causas primárias após escolher responsabilidade */
  useEffect(() => {
    let alive = true;
    if (!form.responsabilidade) {
      setPrimarias([]); setSecundarias([]); return;
    }
    (async () => {
      try {
        const list = await getCausasPrimarias(form.responsabilidade);
        if (alive) setPrimarias(uniqSorted(list));
      } catch {
        if (alive) setPrimarias([]);
      } finally {
        if (alive) setSecundarias([]);
      }
    })();
    return () => { alive = false; };
  }, [form.responsabilidade]);

  /* Causas secundárias após escolher impacto */
  useEffect(() => {
    let alive = true;
    if (!form.responsabilidade || !form.impacto) {
      setSecundarias([]); return;
    }
    (async () => {
      try {
        const list = await getCausasSecundarias(form.responsabilidade, form.impacto);
        if (alive) setSecundarias(uniqSorted(list));
      } catch {
        if (alive) setSecundarias([]);
      }
    })();
    return () => { alive = false; };
  }, [form.responsabilidade, form.impacto]);

  /* Form handlers */
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "responsabilidade") {
      setForm((f) => ({ ...f, responsabilidade: value, impacto: "", causa: "" }));
    } else if (name === "impacto") {
      setForm((f) => ({ ...f, impacto: value, causa: "" }));
    } else {
      setForm((f) => ({ ...f, [name]: value }));
    }
  };

  const canConfirm = useMemo(() => {
    const { responsabilidade, impacto, causa, justificativa } = form;
    return (
      !!responsabilidade &&
      !!impacto &&
      !!causa &&
      String(justificativa).trim().length > 0 &&
      !!codigo_unico
    );
  }, [form, codigo_unico]);

  /* Dispara a REPROGRAMAÇÃO (inclui chamada ao backend) */
  const handleConfirm = async (e) => {
    e?.preventDefault?.();
    if (!canConfirm || loading) return;

    try {
      setLoading(true);

      const payload = sanitizePayload({
        codigo_unico,
        etapa_nova: "d1",
        status_programacaod1: "reprogramar",
        motivo_alteracao:
          corrigePT(form.justificativa) || "Reprogramação solicitada",

        // campos de fechamento (desvios)
        responsavel: form.responsabilidade,
        impacto_fechamento: form.impacto,
        causa_fechamento: form.causa,
        justificativa_fechamento: corrigePT(form.justificativa),

        // limpar execução para retornar “limpo” ao D1
        status_finalizacao: null,
        primeira_previa: null,
        segunda_previa: null,
        primeiro_inicio: null,
        primeiro_fim: null,
        segundo_inicio: null,
        segundo_fim: null,
        terceiro_inicio: null,
        terceiro_fim: null,
        quantidade_real: null,
        tempo_total: null,
      });

      await salvarExecucao(payload);

      // compat: se o chamador quiser inspecionar o form
      if (typeof onConfirm === "function") onConfirm({ ...form, codigo_unico });

      onSaved();     // avisa o pai para recarregar
      onClose();     // fecha o modal
    } catch (e2) {
      alert("Erro ao reprogramar: " + (e2?.response?.data?.error || e2.message || "Erro desconhecido"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="reprog-modal__backdrop" onClick={(e) => e.stopPropagation()}>
      <div
        className="reprog-modal__container"
        role="dialog"
        aria-modal="true"
        aria-label="Justificativa para Reprogramação"
      >
        {/* Header */}
        <div className="reprog-modal__header">
          <h3 className="reprog-modal__title">Justificativa para Reprogramação</h3>
          <button
            className="reprog-modal__close"
            aria-label="Fechar"
            type="button"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <form className="reprog-modal__body" onSubmit={handleConfirm}>
          <div className="reprog-modal__grid">
            <div className="reprog-field">
              <div className="reprog-label">
                RESPONSABILIDADE <span className="req">*</span>
              </div>
              <div className="reprog-val">
                <select
                  name="responsabilidade"
                  value={form.responsabilidade}
                  onChange={handleChange}
                  required
                >
                  <option value="" disabled>Selecione a responsabilidade</option>
                  {causadores.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="reprog-field">
              <div className="reprog-label">
                IMPACTO <span className="req">*</span>
              </div>
              <div className="reprog-val">
                <select
                    name="impacto"
                    value={form.impacto}
                    onChange={handleChange}
                    disabled={!form.responsabilidade}
                    required
                  >
                  <option value="" disabled>Selecione o impacto</option>
                  {primarias.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="reprog-field">
              <div className="reprog-label">
                CAUSA <span className="req">*</span>
              </div>
              <div className="reprog-val">
                <select
                  name="causa"
                  value={form.causa}
                  onChange={handleChange}
                  disabled={!form.responsabilidade || !form.impacto}
                  required
                >
                  <option value="" disabled>Selecione a causa</option>
                  {secundarias.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="reprog-field reprog-span-3">
              <div className="reprog-label">
                JUSTIFICATIVA <span className="req">*</span>
              </div>
              <div className="reprog-val">
                <textarea
                  name="justificativa"
                  placeholder="Explique resumidamente a justificativa da reprogramação"
                  value={form.justificativa}
                  onChange={handleChange}
                  rows={4}
                  required
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="reprog-modal__footer">
            <button className="reprog-btn -ghost" type="button" onClick={onClose}>
              Cancelar
            </button>
            <button
              className="reprog-btn"
              type="submit"
              disabled={!canConfirm || loading}
              aria-disabled={!canConfirm || loading}
            >
              {loading ? "Enviando..." : "Confirmar Reprogramação"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
